import { Settings } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'mf_notable_option_min_premium_usd';

export function readNotableOptionMinPremiumUsd(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const n = raw != null ? Number(raw) : NaN;
    if (NOTABLE_OPTION_MIN_PREMIUM_PRESETS.some((p) => p.value === n)) return n;
  } catch {
    /* ignore */
  }
  return NOTABLE_OPTION_MIN_PREMIUM_PRESETS[0].value;
}

export function persistNotableOptionMinPremiumUsd(usd: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(usd));
  } catch {
    /* ignore */
  }
}

export const NOTABLE_OPTION_MIN_PREMIUM_PRESETS = [
  { label: '$25k+', value: 25_000 },
  { label: '$50k+', value: 50_000 },
  { label: '$100k+', value: 100_000 },
  { label: '$250k+', value: 250_000 },
] as const;

export function NotableOptionTradesLayerSettings({
  layerActive,
  minPremiumUsd,
  onMinPremiumChange,
}: {
  layerActive: boolean;
  minPremiumUsd: number;
  onMinPremiumChange: (usd: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!layerActive) return null;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        aria-label="Notable option trades premium filter"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-fuchsia-400 ${
          open ? 'bg-fuchsia-50 text-fuchsia-800' : 'text-fuchsia-700 hover:bg-fuchsia-50'
        }`}
      >
        <Settings className="h-3.5 w-3.5" />
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Minimum option premium"
          className="absolute left-0 top-full z-50 mt-1 w-44 rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="mb-1.5 px-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Min premium
          </p>
          <ul className="space-y-0.5">
            {NOTABLE_OPTION_MIN_PREMIUM_PRESETS.map((p) => (
              <li key={p.value}>
                <button
                  type="button"
                  onClick={() => {
                    onMinPremiumChange(p.value);
                    setOpen(false);
                  }}
                  className={`w-full rounded-md px-2 py-1.5 text-left text-xs transition-colors ${
                    minPremiumUsd === p.value
                      ? 'bg-fuchsia-600 font-medium text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {p.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
